import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import BusinessPartnerService, {
  BusinessPartner,
  PartnerKind,
  CreateBusinessPartnerPayload,
} from '../services/BusinessPartnerService';
import PrimaryButton from '../../../components/PrimaryButton';

interface ContactModalProps {
  isOpen: boolean;
  contact: BusinessPartner | null;
  onClose: () => void;
  onSave: (contact: BusinessPartner) => void;
}

const KIND_OPTIONS: { value: PartnerKind; label: string }[] = [
  { value: 'CUSTOMER', label: 'Customer' },
  { value: 'PARTNER', label: 'Partner' },
  { value: 'SUPPLIER', label: 'Supplier' },
];

export default function ContactModal({
  isOpen,
  contact,
  onClose,
  onSave,
}: ContactModalProps): React.JSX.Element | null {
  const [formData, setFormData] = useState<CreateBusinessPartnerPayload>({
    kind: 'CUSTOMER',
    is_company: true,
    company_name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  });
  const [saving, setSaving] = useState(false);

  const isEdit = !!contact;

  useEffect(() => {
    if (contact) {
      setFormData({
        kind: contact.kind,
        is_company: contact.is_company,
        company_name: contact.company_name || '',
        first_name: contact.first_name || '',
        last_name: contact.last_name || '',
        street: contact.street || '',
        house_no: contact.house_no || '',
        postal_code: contact.postal_code || '',
        city: contact.city || '',
        country: contact.country || '',
        email: contact.email || '',
        phone: contact.phone || '',
        mobile: contact.mobile || '',
        notes: contact.notes || '',
      });
    } else {
      setFormData({
        kind: 'CUSTOMER',
        is_company: true,
        company_name: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        mobile: '',
      });
    }
  }, [contact, isOpen]);

  const handleChange = (key: keyof CreateBusinessPartnerPayload, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation A: kind must be selected
    if (!formData.kind) {
      toast.error('Please select a contact type');
      return;
    }

    // Validation B: Company requires company_name
    if (formData.is_company && !formData.company_name?.trim()) {
      toast.error('Company name is required for company contacts');
      return;
    }

    // Validation C: Individual requires at least first_name OR last_name
    if (!formData.is_company && !formData.first_name?.trim() && !formData.last_name?.trim()) {
      toast.error('Please enter at least a first or last name');
      return;
    }

    // Build payload - only include non-empty optional fields
    // Backend may reject empty strings for optional fields
    const payload: CreateBusinessPartnerPayload = {
      kind: formData.kind,
      is_company: formData.is_company,
    };

    // Add optional fields only if they have values
    if (formData.is_company && formData.company_name?.trim()) {
      payload.company_name = formData.company_name.trim();
    }
    if (formData.first_name?.trim()) {
      payload.first_name = formData.first_name.trim();
    }
    if (formData.last_name?.trim()) {
      payload.last_name = formData.last_name.trim();
    }
    if (formData.email?.trim()) {
      payload.email = formData.email.trim();
    }
    if (formData.phone?.trim()) {
      payload.phone = formData.phone.trim();
    }
    if (formData.mobile?.trim()) {
      payload.mobile = formData.mobile.trim();
    }
    if (formData.street?.trim()) {
      payload.street = formData.street.trim();
    }
    if (formData.house_no?.trim()) {
      payload.house_no = formData.house_no.trim();
    }
    if (formData.postal_code?.trim()) {
      payload.postal_code = formData.postal_code.trim();
    }
    if (formData.city?.trim()) {
      payload.city = formData.city.trim();
    }
    if (formData.country?.trim()) {
      payload.country = formData.country.trim();
    }
    if (formData.tax_number?.trim()) {
      payload.tax_number = formData.tax_number.trim();
    }
    if (formData.vat_id?.trim()) {
      payload.vat_id = formData.vat_id.trim();
    }
    if (formData.notes?.trim()) {
      payload.notes = formData.notes.trim();
    }

    setSaving(true);
    try {
      let result: BusinessPartner;
      if (isEdit && contact) {
        result = await BusinessPartnerService.update(contact.id, payload);
        toast.success('Contact updated');
      } else {
        result = await BusinessPartnerService.create(payload);
        toast.success('Contact created');
      }
      onSave(result);
    } catch (err: any) {
      console.error('Failed to save contact:', err);
      console.error('Backend says:', JSON.stringify(err?.response?.data, null, 2));
      console.error('Payload sent:', JSON.stringify(payload, null, 2));

      const responseData = err?.response?.data;
      let message = 'Save failed';

      if (responseData) {
        if (responseData.detail) {
          message = responseData.detail;
        } else if (responseData.non_field_errors) {
          message = responseData.non_field_errors.join(', ');
        } else if (typeof responseData === 'object') {
          const errors = Object.entries(responseData)
            .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
            .join('; ');
          if (errors) message = errors;
        }
      }

      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e0e3ef',
    borderRadius: '6px',
    fontSize: '14px',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '6px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#444',
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '600px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: '1px solid #e0e3ef',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#021a4a' }}>
            {isEdit ? 'Edit contact' : 'New contact'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              color: '#666',
            }}
          >
            <FaTimes size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          {/* Kind */}
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Type</label>
            <select
              value={formData.kind}
              onChange={(e) => handleChange('kind', e.target.value as PartnerKind)}
              disabled={isEdit}
              style={{
                ...inputStyle,
                background: isEdit ? '#f5f5f5' : 'white',
                cursor: isEdit ? 'not-allowed' : 'pointer',
              }}
            >
              {KIND_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Is Company Toggle */}
          <div style={{ marginBottom: '20px' }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              <input
                type="checkbox"
                checked={formData.is_company}
                onChange={(e) => handleChange('is_company', e.target.checked)}
                style={{ width: '18px', height: '18px' }}
              />
              <span>Company (not individual)</span>
            </label>
          </div>

          {/* Company Name */}
          {formData.is_company && (
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>
                Company name <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="text"
                value={formData.company_name}
                onChange={(e) => handleChange('company_name', e.target.value)}
                placeholder="ACME Corp"
                style={inputStyle}
              />
            </div>
          )}

          {/* First Name / Last Name */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>
                First name
                {formData.is_company ? (
                  <span style={{ color: '#888', fontSize: '11px', marginLeft: '4px' }}>(Contact person)</span>
                ) : null}
              </label>
              <input
                type="text"
                value={formData.first_name}
                onChange={(e) => handleChange('first_name', e.target.value)}
                placeholder={formData.is_company ? 'Contact person first name' : 'John'}
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>
                Last name
                {formData.is_company ? (
                  <span style={{ color: '#888', fontSize: '11px', marginLeft: '4px' }}>(Contact person)</span>
                ) : null}
              </label>
              <input
                type="text"
                value={formData.last_name}
                onChange={(e) => handleChange('last_name', e.target.value)}
                placeholder={formData.is_company ? 'Contact person last name' : 'Doe'}
                style={inputStyle}
              />
            </div>
          </div>
          {!formData.is_company && (
            <p style={{ margin: '-12px 0 20px 0', fontSize: '12px', color: '#666' }}>
              * At least first name or last name is required
            </p>
          )}

          {/* Email */}
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="contact@company.com"
              style={inputStyle}
            />
          </div>

          {/* Phone / Mobile */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="+49 30 12345678"
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Mobile</label>
              <input
                type="tel"
                value={formData.mobile}
                onChange={(e) => handleChange('mobile', e.target.value)}
                placeholder="+49 170 1234567"
                style={inputStyle}
              />
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '10px 24px',
                border: '1px solid #e0e3ef',
                borderRadius: '10px',
                background: 'white',
                color: '#666',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <PrimaryButton type="submit" disabled={saving} style={{ padding: '10px 24px' }}>
              {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </div>
  );
}
